import { useQuery } from '@tanstack/react-query';
import type { ApprovalRequestRow, Paged } from '@nexops/shared';
import { api } from '@/shared/lib/api';
import { useLeavePendingCount } from '@/features/leave/leave.api';
import { usePermissionChecker } from '@/features/auth/permissions';
import { useChatUnread } from '@/features/chat/hooks/useConversations';
import { useNotificationSummary } from '@/features/notifications/hooks/useNotificationSummary';
import { useOrderQueueCount } from '@/features/sales-orders/salesOrders.api';

/**
 * Liczniki przy pozycjach menu (klucz = `NavItem.key`): nieprzeczytane powiadomienia, akceptacje czekające na mnie
 * (+ moje wnioski zwrócone do poprawy: `workflowReturned`) i czat. Brak endpointu / uprawnień → cicho brak licznika.
 */
export function useNavBadges(): Record<string, number> {
  const can = usePermissionChecker();

  const notifications = useNotificationSummary();

  const approvals = useQuery({
    queryKey: ['nav-badges', 'workflow'],
    queryFn: () => api.get<Paged<ApprovalRequestRow>>('/workflow/requests', { mine: 'pending', page: 1, pageSize: 1 }),
    enabled: can(['workflow.start', 'workflow.approve.prices', 'workflow.approve.logistics', 'workflow.approve.orders', 'workflow.approve.credit']),
    refetchInterval: 60_000,
    retry: false,
    meta: { silent: true },
  });

  // Requests I submitted that a department returned for completion — they need my action too.
  const returned = useQuery({
    queryKey: ['nav-badges', 'workflow-returned'],
    queryFn: () => api.get<Paged<ApprovalRequestRow>>('/workflow/requests', { mine: 'requested', status: 'RETURNED', page: 1, pageSize: 1 }),
    enabled: can('workflow.start'),
    refetchInterval: 60_000,
    retry: false,
    meta: { silent: true },
  });

  const chat = useChatUnread(can('chat.use'));
  // Zamówienia od handlowców do wprowadzenia do Subiektu (NX-419) — tylko Biuro zamówień.
  const salesOrders = useOrderQueueCount(can('orders.manage'));
  // NX-421: wnioski urlopowe i inne czekające na moją decyzję (widok Urlopy → Wnioski).
  const leave = useLeavePendingCount(can('leave.view'));

  return {
    notifications: notifications.data?.unread ?? 0,
    workflow: (approvals.data?.total ?? 0) + (returned.data?.total ?? 0),
    workflowPending: approvals.data?.total ?? 0,
    workflowReturned: returned.data?.total ?? 0,
    chat: chat.data?.total ?? 0,
    salesOrders: salesOrders.data?.toEnter ?? 0,
    leave: leave.data?.count ?? 0,
  };
}
